import {patchState, signalStore, withMethods, withState} from '@ngrx/signals';
import {computed, inject} from '@angular/core';
import {
  API_BASE_URL,
  ImageClient,
  ImageDto,
  ImageSortEnum,
  FileParameter
} from '../../core/services/service-clients';
import {MessageService} from 'primeng/api';
import {firstValueFrom} from 'rxjs';
import {HttpClient} from '@angular/common/http';

// Interface for the store state
interface ImageState {
  images: ImageDto[];
  imageCount: number;
  loading: boolean;
  lastUploadedImage: ImageDto | null;
}

// Initial state
const initialState: ImageState = {
  images: [],
  imageCount: 0,
  loading: false,
  lastUploadedImage: null,
};

function extractErrorMessage(err: unknown): string | null {
  // Backend returns 400 with { message, customError } body for known failures
  const e = err as { response?: string; message?: string };
  if (typeof e?.response === 'string') {
    try {
      const parsed = JSON.parse(e.response);
      if (parsed && typeof parsed.message === 'string') return parsed.message;
    } catch {
      return e.response;
    }
  }
  if (typeof e?.message === 'string') return e.message;
  return null;
}

export const imageStore = signalStore(
  withState<ImageState>(initialState),

  withMethods((store) => {
    const imageClient = inject(ImageClient);
    const messageService = inject(MessageService);
    const http = inject(HttpClient);
    const baseUrl = inject(API_BASE_URL, {optional: true}) ?? '';

    return {
      // Load images with pagination and sorting
      async loadImages(skip: number, take: number, sortBy: ImageSortEnum, isDescending: boolean, search: string): Promise<boolean> {
        patchState(store, {loading: true});
        try {
          const images = await firstValueFrom(imageClient.getImagesData(skip, take, sortBy, isDescending, search));
          const count = await firstValueFrom(imageClient.getImageCount(search));

          patchState(store, {
            images,
            imageCount: count,
            loading: false,
          });
          return true;
        } catch {
          patchState(store, {loading: false});
          return false;
        }
      },

      // Load single image
      async loadImage(imageId: number): Promise<boolean> {
        patchState(store, {loading: true});
        try {
          const image = await firstValueFrom(imageClient.getImageData(imageId));
          patchState(store, {
            images: [image],
            loading: false
          });
          return true;
        } catch {
          patchState(store, {loading: false});
          return false;
        }
      },

      // Upload image
      async uploadImage(title: string, altText: string, fileName: string, file: FileParameter): Promise<ImageDto | null> {
        patchState(store, {loading: true});
        try {
          const image = await firstValueFrom(imageClient.post(title, altText, fileName, file));
          patchState(store, {
            images: [...store.images(), image],
            loading: false,
            lastUploadedImage: image
          });
          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Bild wurde erfolgreich hochgeladen'
          });
          return image;
        } catch (err: unknown) {
          patchState(store, {loading: false});
          const message = extractErrorMessage(err) ?? 'Bild konnte nicht hochgeladen werden';
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: message
          });
          throw new Error(message, {cause: err});
        }
      },

      // Update image metadata (title / altText)
      // TODO: replace direct HttpClient call with imageClient.put() after running yarn nswag.
      async updateImage(imageId: number, title: string, altText: string): Promise<ImageDto | null> {
        patchState(store, {loading: true});
        try {
          const url = `${baseUrl}/api/Image/${imageId}`;
          const updated = await firstValueFrom(
            http.put<ImageDto>(url, {title, altText})
          );
          const next = ImageDto.fromJS(updated);
          patchState(store, {
            images: store.images().map(img => img.id === imageId ? next : img),
            loading: false
          });
          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Bild wurde aktualisiert'
          });
          return next;
        } catch {
          patchState(store, {loading: false});
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Bild konnte nicht aktualisiert werden'
          });
          return null;
        }
      },

      // Delete image
      async deleteImage(image: ImageDto): Promise<boolean> {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(imageClient.delete(image.id));
          patchState(store, {
            images: store.images().filter(img => img.id !== image.id),
            loading: false
          });
          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Bild wurde erfolgreich gelöscht'
          });
          return true;
        } catch {
          patchState(store, {loading: false});
          return false;
        }
      },

      // Clear last uploaded image
      clearLastUploadedImage(): void {
        patchState(store, { lastUploadedImage: null });
      },

      // Special selector example - if you need computed values
      selectImagesByTitle(title: string) {
        return computed(() =>
          store.images().filter(image =>
            image.title.toLowerCase().includes(title.toLowerCase())
          )
        );
      }
    };
  })
);
